/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Native File System Access API helpers.
 * Enables true local file opening and in-place disk overwriting for Ctrl+S and Save.
 */

export function supportsFileSystemAccess(): boolean {
  return typeof window !== "undefined" && "showOpenFilePicker" in window;
}

export async function pickFileNative(): Promise<{
  file: File;
  handle: FileSystemFileHandle;
} | null> {
  if (!supportsFileSystemAccess()) return null;
  try {
    const win = window as any;
    const [handle] = await win.showOpenFilePicker({
      types: [
        {
          description: "Documents (PDF, DOCX, ODT)",
          accept: {
            "application/pdf": [".pdf"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
              ".docx",
            ],
            "application/vnd.oasis.opendocument.text": [".odt"],
          },
        },
      ],
      multiple: false,
    });
    if (!handle) return null;
    const file = await handle.getFile();
    return { file, handle };
  } catch {
    return null;
  }
}

export async function writeToFileHandle(
  handle: FileSystemFileHandle,
  data: Uint8Array | Blob,
): Promise<boolean> {
  try {
    const h = handle as any;
    if (
      typeof h.queryPermission === "function" &&
      (await h.queryPermission({ mode: "readwrite" })) !== "granted"
    ) {
      if (
        typeof h.requestPermission === "function" &&
        (await h.requestPermission({ mode: "readwrite" })) !== "granted"
      ) {
        return false;
      }
    }
    const writable = await h.createWritable();
    await writable.write(data);
    await writable.close();
    return true;
  } catch (err) {
    console.warn("Direct file write failed:", err);
    return false;
  }
}

export async function saveFileAsNative(
  data: Uint8Array | Blob,
  suggestedName: string,
  mime: string,
): Promise<FileSystemFileHandle | null> {
  if (!supportsFileSystemAccess() || !("showSaveFilePicker" in window))
    return null;
  try {
    const win = window as any;
    const lower = suggestedName.toLowerCase();
    const ext = lower.endsWith(".pdf")
      ? ".pdf"
      : lower.endsWith(".odt")
        ? ".odt"
        : ".docx";
    const description =
      ext === ".pdf"
        ? "PDF Document"
        : ext === ".odt"
          ? "OpenDocument Text"
          : "Word Document";
    const handle = await win.showSaveFilePicker({
      suggestedName,
      types: [
        {
          description,
          accept: { [mime]: [ext] },
        },
      ],
    });
    const success = await writeToFileHandle(handle, data);
    return success ? handle : null;
  } catch {
    return null;
  }
}
